// Admin-only upload UI for "Real Gallery" before/after photos
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { Upload, X, Trash2, Image as ImageIcon, MapPin } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB per image

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminGalleryUploader() {
  const { t } = useLang();
  const ct = t.gallery;

  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [before, setBefore] = useState(null); // {dataUrl, name}
  const [after, setAfter] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/gallery");
      setItems(r.data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("gallery load failed", err);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (e, setter, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`${label}: max 5 MB`);
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setter({ dataUrl, name: file.name });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !service.trim() || !before || !after) {
      toast.error("Title, service, and both images are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/gallery", {
        title: title.trim(),
        service: service.trim(),
        location: location.trim(),
        description: description.trim(),
        before_image: before.dataUrl,
        after_image: after.dataUrl,
      });
      toast.success("Item added.");
      setTitle("");
      setService("");
      setLocation("");
      setDescription("");
      setBefore(null);
      setAfter(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="border border-neutral-200 bg-white" data-testid="admin-gallery-panel">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-3 bg-neutral-50">
        <ImageIcon size={18} className="text-[#FF5A00]" />
        <h2 className="text-base font-black tracking-tight">Vorher / Nachher · Galerie</h2>
        <span className="text-xs text-neutral-500">({items.length})</span>
      </div>

      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-neutral-200">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel (z. B. Hausreinigung Eppendorf)" className="border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A00]" data-testid="gallery-form-title" />
        <input value={service} onChange={(e) => setService(e.target.value)} placeholder="Service (z. B. Reinigung)" className="border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A00]" data-testid="gallery-form-service" />
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ort (optional)" className="border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A00]" data-testid="gallery-form-location" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Beschreibung (optional)" rows={2} className="border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A00] md:col-span-2 resize-none" data-testid="gallery-form-description" />

        <FileSlot label="Vorher-Bild" file={before} onChange={(e) => handleFile(e, setBefore, "Vorher")} onClear={() => setBefore(null)} testid="gallery-form-before" />
        <FileSlot label="Nachher-Bild" file={after} onChange={(e) => handleFile(e, setAfter, "Nachher")} onClear={() => setAfter(null)} testid="gallery-form-after" />

        <button type="submit" disabled={submitting} className="md:col-span-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wider uppercase text-xs px-5 py-3 transition disabled:opacity-60" data-testid="gallery-form-submit">
          <Upload size={14} className="inline me-2 -mt-0.5" />
          {submitting ? "Lädt hoch…" : "Eintrag hinzufügen"}
        </button>
      </form>

      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && <p className="text-sm text-neutral-400 col-span-full">Noch keine Einträge.</p>}
        {items.map((it) => (
          <div key={it.item_id} className="border border-neutral-200 overflow-hidden relative group">
            <button type="button" onClick={() => removeItem(it.item_id)} className="absolute top-2 end-2 z-10 w-7 h-7 bg-white/90 hover:bg-rose-500 hover:text-white text-neutral-700 flex items-center justify-center" aria-label="Delete" data-testid={`gallery-delete-${it.item_id}`}>
              <Trash2 size={12} />
            </button>
            <div className="grid grid-cols-2 aspect-[16/9]">
              <img src={it.before_image} alt="Before" className="w-full h-full object-cover" />
              <img src={it.after_image} alt="After" className="w-full h-full object-cover" />
            </div>
            <div className="p-3">
              <p className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">{it.service}</p>
              <h3 className="font-black text-sm truncate">{it.title}</h3>
              {it.location && <p className="text-[11px] text-neutral-500 mt-0.5 inline-flex items-center gap-1"><MapPin size={10} />{it.location}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FileSlot({ label, file, onChange, onClear, testid }) {
  return (
    <label className="border-2 border-dashed border-neutral-300 hover:border-[#FF5A00] px-4 py-3 text-sm cursor-pointer flex items-center gap-3 transition">
      <Upload size={14} className="text-neutral-500" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-xs tracking-widest uppercase text-neutral-500">{label}</div>
        <div className="text-xs truncate">{file ? file.name : "Bild auswählen (max. 5 MB)"}</div>
      </div>
      {file && (
        <button type="button" onClick={(e) => { e.preventDefault(); onClear(); }} className="text-neutral-400 hover:text-rose-500" aria-label="Clear">
          <X size={14} />
        </button>
      )}
      <input type="file" accept="image/*" onChange={onChange} className="hidden" data-testid={testid} />
    </label>
  );
}
